/**
 * Dedicated, browser-only Apollo client for the beancount.io API gateway.
 *
 * The app's primary Apollo client (see `src/common/apollo/client.ts`) talks to
 * this site's own GraphQL endpoint. The pricing page's subscription/checkout
 * flow, however, targets the separate beancount.io api-gateway. Authentication
 * is the HttpOnly `authSess:beancount.io` session cookie the dashboard's
 * backend sets: `credentials: "same-origin"` attaches it to every request, so
 * this client never needs to (and cannot) read it. The localStorage Bearer
 * token below is the *legacy* dashboard's mechanism, kept only so a stale
 * session from before the cookie migration still works. This client is created
 * lazily and only ever on the client, so it never runs during SSR.
 */

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
} from "@apollo/client";

/** localStorage key the beancount.io dashboard stores the auth token under. */
export const AUTH_TOKEN_KEY = "authToken:beancount.io";

const BEANCOUNT_API_URL =
  import.meta.env.VITE_BEANCOUNT_API_URL ||
  "https://beancount.io/api-gateway/";

let client: ApolloClient | undefined;

/**
 * Returns the memoised beancount.io Apollo client. Must only be called in the
 * browser (it reads localStorage for the auth token).
 */
export function getBeancountClient(): ApolloClient {
  if (client) return client;

  const httpLink = new HttpLink({
    uri: BEANCOUNT_API_URL,
    credentials: "same-origin",
  });

  const authLink = new ApolloLink((operation, forward) => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem(AUTH_TOKEN_KEY)
        : null;
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }));
    return forward(operation);
  });

  client = new ApolloClient({
    link: from([authLink, httpLink]),
    cache: new InMemoryCache(),
  });
  return client;
}
